import React, { useState, useEffect, ChangeEvent, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { trackDestinationUpdated } from '../../analytics/analytics';
import DestinationDetailsModal from "./DestinationDetailsModal";
import { Connection, ConnectionConfiguration, Destination, Organization, buildDestination } from "../common/ObjectTypes";
import { getConnectionsList, testConnectionAction } from "../../api/connections/connectionsApi";
import { getDestinationDetails, updateDestination } from "../../api/destinations/destinationsApi";
import { toast } from "react-toastify";
import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import addKeywords from 'ajv-keywords';
import { loadOrganizations } from "../../api/organizations/loadOrganizations";
import { filter_page_size, page_size } from "../../api/apiUtils";
import { useParams, useLocation } from "react-router-dom";
import { useKeycloakToken } from "../common/useKeycloak";
import { useTranslation } from "react-i18next";
import { isHiddenUrlDestinationType } from "./isHiddenUrlDestinationType";
import { loadCurrentUserDetails } from "../organizations/loadCurrentUserDetails";

export function ManageDestinationDetails() {
    const { keycloak } = useKeycloak();
    const { t } = useTranslation();
    const [token] = useKeycloakToken();
    const { dest_id } = useParams();
    const location = useLocation();
    // Determine subpage from the URL path
    const isConfig = location.pathname.endsWith('/configuration');
    const currentSubPage = isConfig ? 'configuration' : 'details';
    const navigate = useNavigate();
    const [currentMemberIsAdmin, setCurrentMemberIsAdmin] = useState<boolean>(false);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [destination, setDestination] = useState<Destination>(buildDestination())
    const [destinationTitle, setDestinationTitle] = useState<string>("")
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [orderingString, setOrderingString] = useState("");
    const [loadingConnections, setLoadingConnections] = useState(true);
    const [selectedDestConnections, setSelectedDestConnections] = useState<Connection[]>([]);
    const [updatedConfigurations, setUpdatedConfigurations] = useState<any[]>([]);
    const [testActionLoading, setTestActionLoading] = useState(false);
    const [testActionData, setTestActionData] = useState<any>(null);
    const [testActionStatus, setTestActionStatus] = useState<number>(0);
    const [errors, setErrors] = useState({});
    const [rsjfErrors, setRsjfErrors] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [offset, setOffset] = useState<number>(0);
    const [orgSearchInput, setOrgSearchInput] = useState<string>("");
    const [reloadDestination, setReloadDestination] = useState<boolean>(false);
    // Ref for scrollable content
    const scrollableContentRef = useRef<HTMLDivElement | null>(null);

    const onToggleModal = () => {
        const pathArray = window.location.pathname.split('/').filter(Boolean);

        // New routes: /destinations/:dest_id and /destinations/:dest_id/configuration
        const destinationsIndex = pathArray.indexOf('destinations');
        if (destinationsIndex > -1) {
            // If on configuration, go back to /destinations/:dest_id
            if (pathArray[pathArray.length - 1] === 'configuration' && pathArray.length >= destinationsIndex + 2) {
                const destId = pathArray[destinationsIndex + 1];
                navigate(`/destinations/${destId}`);
                return;
            }
            // Otherwise go back to /destinations list
            navigate('/destinations');
            return;
        }

        // Legacy routes: /destination/:dest_id and nested /.../destination/:dest_id
        const destinationIndex = pathArray.indexOf('destination');
        if (destinationIndex > -1) {
            const newPathArray = pathArray.slice(0, destinationIndex);
            const newPath = `/${newPathArray.join('/')}` || '/';
            navigate(newPath);
        }
    }

    const loadOrganizationsData = async (search_str: string = "") => {
        const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
        if (!isLoggedIn || !keycloak.token) return;
        try {
            if (search_str) {
                const [orgs, , error] = await loadOrganizations(keycloak.token, 0, search_str, []);
                if (!error) setOrganizations(orgs);
            } else {
                let accumulated: Organization[] = [];
                let offset = 0;
                while (true) {
                    const [orgs, total, error] = await loadOrganizations(keycloak.token, offset, "", accumulated);
                    if (error) break;
                    accumulated = orgs;
                    if (offset + filter_page_size >= total) break;
                    offset += filter_page_size;
                }
                setOrganizations(accumulated);
            }
        } catch (err) {
            console.log('Failed to load organizations:', err);
        }
    };

    useEffect(() => {
        const loadSelectedDestinationConnections = async () => {
            // Check if user is authenticated before making API calls
            const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
            if (!isLoggedIn) {
                return;
            }

            if (keycloak.token) {
                try {
                    const connectionFilterQuery = {
                        destination_id: dest_id,
                        ordering: orderingString,
                        offset: offset
                    }
                    const temp_connections = await getConnectionsList(keycloak.token, connectionFilterQuery)
                    setSelectedDestConnections(temp_connections.results);
                    setTotalCount(temp_connections.count);
                    setLoadingConnections(false);
                    setPageLoaded(true);
                } catch (err) {
                    console.log(err)
                }
            }
        };

        const loadDestinationDetails = async () => {
            // Check if user is authenticated before making API calls
            const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
            if (!isLoggedIn) {
                return;
            }

            if (keycloak.token && dest_id) {
                try {
                    const temp = await getDestinationDetails(keycloak.token, dest_id);
                    // Check if temp is defined and has required properties
                    if (!temp || !temp.owner || !temp.owner.id) {
                        return;
                    }
                    const temp_user = await loadCurrentUserDetails(temp.owner.id, keycloak.token);
                    setCurrentMemberIsAdmin(temp_user);
                    loadSelectedDestinationConnections();
                    setDestination(temp);
                    setDestinationTitle(temp.name);
                    if (reloadDestination) setReloadDestination(false);
                } catch (err) {
                    // Silently handle errors - they're already handled by the interceptor
                    console.log('Failed to load destination details:', err);
                }
            }
        };

        loadDestinationDetails();
        if (!pageLoaded) loadOrganizationsData(); // load once on mount; orgs are stable for the session
    }, [reloadDestination, dest_id, keycloak.token, offset, orderingString]);

    const handlePageClick = (e: any) => {
        setOffset(e.selected * page_size);
        setLoadingConnections(true);
    }

    const requestSortSelectedDestinationConnection = (orderBy: string, sortDirection: boolean) => {
        const ordering = sortDirection ? orderBy : "-" + orderBy;
        setOrderingString(ordering);
        setLoadingConnections(true);
    }

    const onChangeSelectedDestination = (e: any) => {
        const attr: string = e.target.id
        setDestination((prevDestination: Destination) => ({
            ...prevDestination,
            [attr]: e.target.value
        }))
    }

    const onChangeSelectedDestinationOwner = (e: any) => {
        setDestination((prevDestination: Destination) => ({
            ...prevDestination,
            "owner": {
                name: e.label,
                id: e.value,
                description: ""
            },
        }))
    }

    const onChangeSelectedDestinationConfig = (e: ChangeEvent<HTMLInputElement>, action_id: string) => {
        const config_id: any = destination.configurations && destination.configurations.find((a: any) => a.action.id === action_id)
        let newConfig: any;
        if (config_id) {
            newConfig = {
                "action_id": action_id,
                "id": config_id.id,
                "data": e
            }
        } else {
            newConfig = {
                "action_id": action_id,
                "data": e,
                "id": action_id,
            }
        }
        if (e) {
            const index_to_find = updatedConfigurations.findIndex((a: ConnectionConfiguration) => (config_id && a.id === config_id.id) || a.action_id === action_id)
            if (index_to_find === -1) {

                // if this is a new config id, push it to the array
                setUpdatedConfigurations((prevArray: any[]) => [...prevArray, newConfig])
            } else {

                // if it is an existing config id, replace the old config with the new one
                const tempArray = updatedConfigurations
                tempArray[index_to_find] = newConfig
                setUpdatedConfigurations(tempArray)
            }

        }
    }

    const handleTestAction = async (action_id: string, action_type: string) => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setTestActionLoading(true);
            setTestActionStatus(0);
            // configuration form data for the relevant action
            let form_data = {}
            // find the index in updatedConfigurations that matches the action_id
            let updated_config_index = updatedConfigurations.findIndex((a: any) => a.action_id === action_id)
            // save the data from the updatedConfigurations array if it exists
            if (updated_config_index !== -1) {
                form_data = updatedConfigurations[updated_config_index].data
            } else {
                form_data = destination.configurations.findIndex((a: ConnectionConfiguration) => a.action.id === action_id) !== -1 ? destination.configurations.find((a: ConnectionConfiguration) => a.action.id === action_id).data : {}
            }
            try {
                let result = await testConnectionAction(token, destination.id, action_type, { config_overrides: Object(form_data) });
                if (result) {
                    let { data: resp_data, status } = result;
                    setTestActionData(resp_data);
                    setTestActionStatus(status);
                }
            } catch (err: any) {
                setTestActionStatus(err?.response?.status || 500);
                setTestActionData({ error: err?.response?.data?.detail || err?.message || "Server error. Please try again." });
            } finally {
                setTestActionLoading(false);
            }
        }
    }

    const formIsValid = () => {
        let form_errors: any = {};
        let rsjf_errors: any = {};
        const shouldRequireUrl = !isHiddenUrlDestinationType(destination.type.name);
        if (shouldRequireUrl && destination.base_url.length === 0) {
            form_errors.base_url = "URL is required";
        }
        if (destination.base_url.length > 0) {
            try {
                new URL(destination.base_url);
            } catch (err) {
                form_errors.base_url = "Enter a valid URL including the scheme.";
            }
        }
        if (destination.name.length === 0) {
            form_errors.name = "Name is required";
        }
        setErrors(form_errors);
        setRsjfErrors([])
        updatedConfigurations.forEach((config: any, index: number) => {
            const schema_index = destination.type.actions.findIndex((a: any) => a.id === config.action_id);
            const ajv = new Ajv({ allErrors: true, verbose: true, strictTypes: false });
            ajv.addKeyword({
                keyword: 'is_executable',
                validate: function () {
                    return true;
                },
                errors: false
            });
            ajv.addKeyword({
                keyword: "example",
                type: "string",
                schemaType: ["string", "object", "number", "array"],
                validate: () => true,
            });
            addFormats(ajv);
            addKeywords(ajv);

            let schema = destination.type.actions[schema_index].schema;

            const data = { ...config.data };

            if (schema.properties && typeof schema.properties === 'object') {
                Object.keys(schema.properties).forEach((key) => {
                    if (!data.hasOwnProperty(key)) {
                        data[key] = null;
                    } else {
                        if (data[key] === undefined) {
                            data[key] = null;
                        }
                    }
                });
            } else {
                console.error('schema.properties is not defined or is not an object');
            }
            let modifiedSchema = JSON.parse(JSON.stringify(schema));
            if (schema.if && schema.then && schema.else) {
                // Validate against 'if' condition
                const validateIf = ajv.compile(schema.if);

                if (validateIf(data)) {
                    if (schema.then && typeof schema.then === "object") {
                        modifiedSchema = {
                            ...schema,
                            properties: { ...schema.properties, ...schema.then.properties },
                            required: schema.then.required || [],
                        };
                    }
                } else {
                    if (schema.else && typeof schema.else === "object") {
                        modifiedSchema = {
                            ...schema,
                            properties: { ...schema.properties, ...schema.else.properties },
                            required: schema.else.required || [],
                        };
                    }
                }
                const validateFinal = ajv.compile(modifiedSchema);
                const isValid = validateFinal(data);

                if (!isValid) {
                    validateFinal.errors?.forEach((error: any) => {
                        console.error(`Error in field '${error.instancePath}': ${error.message}`);
                    });
                }
            }

            Object.keys(modifiedSchema.properties).forEach((key) => {
                if (!data.hasOwnProperty(key)) {
                    data[key] = null;
                } else if (data[key] === undefined) {
                    data[key] = null;
                }
            });

            const validateUpdated = ajv.compile(modifiedSchema);
            const valid = validateUpdated(data);
            if (!valid) {
                validateUpdated.errors?.forEach((error: any) => {
                    console.error(`Error in field '${error.instancePath}': ${error.message}`);
                    const field = error.instancePath.slice(1) || error.params.missingProperty;
                    const isFieldRequired = modifiedSchema.required && modifiedSchema.required.includes(field);
                    const hasPattern = modifiedSchema.properties[field]?.pattern;

                    // Skip validation if field is not required and is empty
                    if (!isFieldRequired && (data[field] === null || data[field] === undefined)) {
                        return;
                    }

                    // Validate pattern if defined
                    if (hasPattern && !new RegExp(hasPattern).test(data[field])) {
                        rsjf_errors[schema_index] = rsjf_errors[schema_index] || {};
                        rsjf_errors[schema_index][field] = `Field '${field}' does not match the required pattern.`;
                    }

                    if (!rsjf_errors[destination.type.actions[schema_index].id]) {
                        rsjf_errors[destination.type.actions[schema_index].id] = {};
                    }
                    rsjf_errors[destination.type.actions[schema_index].id][field] = `Error in field '${field}': ${error.message}`;
                });
            }
        });
        setRsjfErrors(rsjf_errors);
        return Object.keys(form_errors).length === 0 && Object.keys(rsjf_errors).length === 0;
    }
    // rsjf_errors[destination.type.actions[schema_index].id]

    // action: 'save' | 'saveAndClose'
    const handleSaveDestination = async (e: React.FormEvent<Element>, action: 'save' | 'saveAndClose' = 'save') => {
        if (!formIsValid()) return;
        else {
            if (keycloak.token) {
                await updateDestination(keycloak.token, destination.id, {
                    "base_url": destination.base_url,
                    "configurations": updatedConfigurations,
                    "enabled": destination.enabled,
                    "owner": destination.owner.id,
                    "name": destination.name
                });
                toast.success(`${t("Destination updated")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
                trackDestinationUpdated(destination.id, destination.type?.name, destination.owner?.name);
                setReloadDestination(true);
                if (action === 'saveAndClose') {
                    onToggleModal();
                } else if (action === 'save') {
                    // Scroll all scrollable content containers to top
                    if (scrollableContentRef.current) {
                        scrollableContentRef.current.scrollTop = 0;
                    }
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
            }
        }
    }

    function handleSearchOrgDropdown(e: any) {
        setOrgSearchInput(e);
        if (e.length > 2) loadOrganizationsData(e);
        else if (e.length === 0) loadOrganizationsData();
    }

    function handleSelectSubPage(e: string) {
        if (!dest_id) return;
        if (e === 'details') {
            // Go to the base details page for this destination
            navigate(`/destinations/${dest_id}`);
        } else {
            // Go to the subpage for this destination
            navigate(`/destinations/${dest_id}/${e}`);
        }
    }

    return (
        <DestinationDetailsModal
            currentMemberIsAdmin={currentMemberIsAdmin}
            destination={destination}
            destinationTitle={destinationTitle}
            destSubPage={currentSubPage}
            onSelectSubPage={handleSelectSubPage}
            organizations={organizations}
            loadMoreOrgs={() => {}}
            orgInputChange={handleSearchOrgDropdown}
            connections={selectedDestConnections}
            onToggleModal={onToggleModal}
            requestSort={requestSortSelectedDestinationConnection}
            loadingConnections={loadingConnections}
            onChangeSelectedDestination={onChangeSelectedDestination}
            onChangeSelectedDestinationOwner={onChangeSelectedDestinationOwner}
            onChangeSelectedDestinationConfig={onChangeSelectedDestinationConfig}
            onTestAction={handleTestAction}
            testActionLoading={testActionLoading}
            testActionResponse={testActionData}
            testActionStatus={testActionStatus}
            onSave={handleSaveDestination}
            errors={errors}
            rsjfErrors={rsjfErrors}
            onPageClicked={handlePageClick}
            totalCount={totalCount}
            pageLoaded={pageLoaded}
            take={page_size}
            offset={offset}
            scrollableContentRef={scrollableContentRef}
        />
    )
}